import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Loading } from '@this/src/components/shared/ui/feedbacks/loading';
import { Button } from '@this/shared/ui/inputs/button';
import { Fetcher, HTTPError } from '@this/src/util';

interface TaskTableProps {
  statusFilter: string;
  onStatusFilterChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  sourceFilter: string;
  onSourceFilterChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  fromAirportFilter: string;
  onFromAirportChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  toAirportFilter: string;
  onToAirportChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  targetDateFilter: string;
  onTargetDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface DomesticAirFetchActionResponse {
  data: DomesticAirFetchAction[];
  total: number;
  page: number;
  per_page: number;
}

interface DomesticAirFetchAction {
  id: number;
  from_airport: string;
  to_airport: string;
  from_date: string;
  to_date: string;
  source: string;
  status: string;
  duration?: number;
  error?: string;
  priority: string;
  fire_reason: string;
  created_at: string;
  updated_at: string;
  assigned_machine_name?: string;
  scheduled_at?: string;
}

interface AirportData {
  from_airports: string[];
  to_airports: string[];
}

const PER_PAGE = 50;

const TaskTable: React.FC<TaskTableProps> = ({
  statusFilter,
  onStatusFilterChange,
  sourceFilter,
  onSourceFilterChange,
  fromAirportFilter,
  onFromAirportChange,
  toAirportFilter,
  onToAirportChange,
  targetDateFilter,
  onTargetDateChange
}) => {
  const [fetchActions, setFetchActions] = useState<DomesticAirFetchAction[]>([]);
  const [fromAirports, setFromAirports] = useState<string[]>([]);
  const [toAirports, setToAirports] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);

  const totalPages = Math.ceil(total / PER_PAGE);

  const handleDeleteAllActions = async () => {
    if (confirm('本当にすべてのタスクを削除しますか？')) {
      try {
        await Fetcher.delete('/god/domestic_air_fetch_actions');
        setFetchActions([]);
        setTotal(0);
        setSelectedIds(new Set());
      } catch (e) {
        setError('タスクデータの削除に失敗しました');
      }
    }
  };

  const handleForceTerminate = async (id: number) => {
    try {
      await Fetcher.put(`/god/domestic_air_fetch_actions/${id}`, {});
      await fetchActionsData();
    } catch (e) {
      setError('タスクの強制終了に失敗しました');
    }
  };

  const handleCancelSelected = async () => {
    if (selectedIds.size === 0) return;
    const queueIds = fetchActions.filter(a => selectedIds.has(a.id) && a.status === 'queue').map(a => a.id);
    if (queueIds.length === 0) {
      setError('キャンセル可能なタスク（処理待ち）が選択されていません');
      return;
    }
    if (!confirm(`${queueIds.length}件のタスクをキャンセルしますか？`)) return;
    try {
      await Fetcher.post('/god/domestic_air_fetch_actions/cancel', { ids: queueIds });
      setSelectedIds(new Set());
      await fetchActionsData();
    } catch (e) {
      setError('タスクのキャンセルに失敗しました');
    }
  };

  const handleCancelSingle = async (id: number) => {
    if (!confirm(`ID ${id} のタスクをキャンセルしますか？`)) return;
    try {
      await Fetcher.post('/god/domestic_air_fetch_actions/cancel', { ids: [id] });
      await fetchActionsData();
    } catch (e) {
      setError('タスクのキャンセルに失敗しました');
    }
  };

  const handleRevive = async (id: number) => {
    if (!confirm(`ID ${id} のタスクを処理待ちに戻しますか？`)) return;
    try {
      await Fetcher.post(`/god/domestic_air_fetch_actions/${id}/revive`, {});
      await fetchActionsData();
    } catch (e) {
      const serverMessage = e instanceof HTTPError ? (e.response?.data?.message as string | undefined) : undefined;
      setError(serverMessage ?? '処理待ちに戻せませんでした');
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const queueIds = fetchActions.filter(a => a.status === 'queue').map(a => a.id);
    const allSelected = queueIds.length > 0 && queueIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(queueIds));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'queue':
        return 'gray';
      case 'started':
        return 'green';
      case 'succeeded':
        return 'blue';
      case 'failed':
      case 'terminated':
      case 'terminated_complete':
        return 'red';
      default:
        return 'transparent';
    }
  };

  const getJpStatusName = (status: string) => {
    switch (status) {
      case 'queue':
        return '処理待ち';
      case 'started':
        return '開始';
      case 'succeeded':
        return '成功';
      case 'failed':
        return '失敗';
      case 'terminated':
        return '強制終了';
      case 'terminated_complete':
        return 'キャンセル';
      default:
        return '未定義';
    }
  };

  const fetchActionsData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (sourceFilter) params.append('source', sourceFilter);
      if (fromAirportFilter) params.append('from_airport', fromAirportFilter);
      if (toAirportFilter) params.append('to_airport', toAirportFilter);
      if (targetDateFilter) params.append('from_date', targetDateFilter);
      params.append('page', page.toString());

      const response = await Fetcher.get<DomesticAirFetchActionResponse>(
        `/god/domestic_air_fetch_actions?${params.toString()}`
      );
      setFetchActions(response.data);
      setTotal(response.total);
      setSelectedIds(new Set());
    } catch (e) {
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter, fromAirportFilter, toAirportFilter, targetDateFilter, page]);

  useEffect(() => {
    fetchActionsData();
  }, [fetchActionsData]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, sourceFilter, fromAirportFilter, toAirportFilter, targetDateFilter]);

  useEffect(() => {
    const fetchAirports = async () => {
      try {
        const response = await Fetcher.get<AirportData>('/god/domestic_air/index_airports');
        setFromAirports(response.from_airports);
        setToAirports(response.to_airports);
      } catch (e) {
        setError('データの取得に失敗しました');
      }
    };

    fetchAirports();
  }, []);

  const styles = useMemo(() => getStyles(), []);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <p>{error}</p>;
  }

  const queueIds = fetchActions.filter(a => a.status === 'queue').map(a => a.id);
  const allQueueSelected = queueIds.length > 0 && queueIds.every(id => selectedIds.has(id));

  return (
    <div style={styles.container}>
      <div style={styles.filterRow}>
        <div>
          <label>ソース:</label>
          <select value={sourceFilter} onChange={onSourceFilterChange}>
            <option value="">すべて</option>
            <option value="jal_official">JAL</option>
            <option value="ana_official">ANA</option>
            <option value="sna_official">SNA</option>
            <option value="ado_official">ADO</option>
            <option value="sfj_official">SFJ</option>
            <option value="orc_official">ORC</option>
            <option value="amx_official">AMX</option>
            <option value="ibx_official">IBX</option>
            <option value="peach_official">PEACH</option>
            <option value="jetstar_from_skyticket">JETSTAR</option>
          </select>
        </div>
        <div>
          <label>ステータス:</label>
          <select value={statusFilter} onChange={onStatusFilterChange}>
            <option value="">すべて</option>
            <option value="queue">処理待ち</option>
            <option value="started">開始</option>
            <option value="succeeded">成功</option>
            <option value="failed">失敗</option>
            <option value="terminated">強制終了</option>
            <option value="terminated_complete">キャンセル</option>
          </select>
        </div>
        <div>
          <label htmlFor="from-airport">出発空港</label>
          <select id="from-airport" value={fromAirportFilter} onChange={onFromAirportChange}>
            <option value="">すべて</option>
            {fromAirports.map((airport, index) => (
              <option key={index} value={airport}>
                {airport}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="to-airport">到着空港</label>
          <select id="to-airport" value={toAirportFilter} onChange={onToAirportChange}>
            <option value="">すべて</option>
            {toAirports.map((airport, index) => (
              <option key={index} value={airport}>
                {airport}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="target-date">取得開始日</label>
          <input type="date" id="target-date" value={targetDateFilter} onChange={onTargetDateChange} />
        </div>
      </div>
      <div style={styles.toolbar}>
        <div>
          {total === 0
            ? '0件'
            : `全${total}件中 ${(page - 1) * PER_PAGE + 1}〜${Math.min(page * PER_PAGE, total)}件表示`}
        </div>
        <div style={styles.toolbarButtons}>
          {selectedIds.size > 0 && (
            <Button onClick={handleCancelSelected}>選択したタスクをキャンセル（{selectedIds.size}件）</Button>
          )}
          <Button onClick={handleDeleteAllActions}>タスクデータをすべて削除</Button>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>
              <input type="checkbox" checked={allQueueSelected} onChange={toggleSelectAll} />
            </th>
            <th>ID</th>
            <th>出発空港</th>
            <th>到着空港</th>
            <th>取得開始日</th>
            <th>取得終了日</th>
            <th>ソース</th>
            <th>ステータス</th>
            <th>経過時間</th>
            <th>エラー</th>
            <th>優先度</th>
            <th>バッチ・オンデマンド</th>
            <th>処理マシン</th>
            <th>実行予定</th>
            <th>作成日時</th>
            <th>更新日時</th>
            <th>アクション</th>
          </tr>
        </thead>
        <tbody>
          {fetchActions.map(action => (
            <tr key={action.id}>
              <td>
                {action.status === 'queue' && (
                  <input
                    type="checkbox"
                    checked={selectedIds.has(action.id)}
                    onChange={() => toggleSelect(action.id)}
                  />
                )}
              </td>
              <td>{action.id}</td>
              <td>{action.from_airport}</td>
              <td>{action.to_airport}</td>
              <td>{action.from_date}</td>
              <td>{action.to_date}</td>
              <td>{action.source}</td>
              <td>
                <div style={styles.statusCell}>
                  <span style={{ ...styles.statusDot, backgroundColor: getStatusColor(action.status) }} />
                  {getJpStatusName(action.status)}
                </div>
              </td>
              <td>{action.duration}</td>
              <td>{action.error}</td>
              <td>{action.priority}</td>
              <td>{action.fire_reason}</td>
              <td>{action.assigned_machine_name}</td>
              <td>{action.scheduled_at || '-'}</td>
              <td>{action.created_at}</td>
              <td>{action.updated_at}</td>
              <td style={styles.actionCell}>
                {action.status === 'queue' ? (
                  <div style={styles.actionButtonGroup}>
                    <Button size="small" style={styles.actionButton} onClick={() => handleCancelSingle(action.id)}>
                      キャンセル
                    </Button>
                    <Button
                      size="small"
                      style={styles.actionButton}
                      onClick={() => handleForceTerminate(action.id)}
                    >
                      強制終了
                    </Button>
                  </div>
                ) : ['started'].includes(action.status) ? (
                  <Button size="small" style={styles.actionButton} onClick={() => handleForceTerminate(action.id)}>
                    強制終了
                  </Button>
                ) : action.status === 'terminated_complete' ? (
                  <Button size="small" style={styles.actionButton} onClick={() => handleRevive(action.id)}>
                    処理待ちに戻す
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={styles.pagination}>
        <Button onClick={() => setPage(1)} disabled={page === 1}>
          {'<<'}
        </Button>
        <Button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
          {'<'}
        </Button>
        <span style={styles.pageInfo}>
          {page} / {Math.max(totalPages, 1)}
        </span>
        <Button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
          {'>'}
        </Button>
        <Button onClick={() => setPage(totalPages)} disabled={page >= totalPages}>
          {'>>'}
        </Button>
      </div>
    </div>
  );
};

const getStyles = (): { [key: string]: React.CSSProperties } => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    margin: '20px auto',
    width: '90%',
    fontSize: '10px'
  },
  filterRow: {
    display: 'flex'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '10px 0'
  },
  toolbarButtons: {
    display: 'flex',
    gap: '8px'
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center'
  },
  statusDot: {
    display: 'inline-block',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    marginRight: '5px'
  },
  actionCell: {
    padding: '8px 10px',
    whiteSpace: 'nowrap'
  },
  actionButtonGroup: {
    display: 'flex',
    gap: '6px'
  },
  actionButton: {
    fontSize: '10px',
    padding: '4px 8px',
    minWidth: 'auto',
    whiteSpace: 'nowrap'
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '20px 0',
    gap: '8px'
  },
  pageInfo: {
    fontSize: '12px'
  }
});

export default observer(TaskTable);
